/**
 * Universal Generate-and-Assemble Video Hook
 * Orchestrates: prerequisites → video generation → waiting → assembly
 *
 * Composes useServerFullVideoGenerator for the assembly phase.
 * Works with any video type via adapter pattern.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useServerFullVideoGenerator } from './useServerFullVideoGenerator';
import type {
  GenerateAndAssemblePhase,
  IGenerateAndAssembleConfig,
  IUseGenerateAndAssembleReturn,
} from '@/@types/generateAndAssemble';

// ============================================
// Progress Mapping
// ============================================

/** Map phase + sub-progress to overall 0-100 progress */
function computeOverallProgress(
  phase: GenerateAndAssemblePhase,
  scenesReadyCount: number,
  scenesTotalCount: number,
  assemblyProgress: number
): number {
  switch (phase) {
    case 'idle':
      return 0;
    case 'prerequisites':
      return 5;
    case 'generating':
      return 12;
    case 'waiting': {
      if (scenesTotalCount === 0) return 15;
      const waitingProgress = scenesReadyCount / scenesTotalCount;
      return Math.round(15 + waitingProgress * 45); // 15-60%
    }
    case 'assembling':
      return Math.round(60 + (assemblyProgress / 100) * 40); // 60-100%
    case 'completed':
      return 100;
    case 'failed':
      return 0;
    default:
      return 0;
  }
}

/** Map phase + counts to a human-readable label */
function computeStatusLabel(
  phase: GenerateAndAssemblePhase,
  scenesReadyCount: number,
  scenesTotalCount: number,
  assemblyProgress: number,
  error: string | null
): string {
  switch (phase) {
    case 'idle':
      return '';
    case 'prerequisites':
      return 'Generating images...';
    case 'generating':
      return 'Starting video generation...';
    case 'waiting':
      return scenesTotalCount > 0
        ? `Generating videos (${scenesReadyCount}/${scenesTotalCount})...`
        : 'Waiting for videos...';
    case 'assembling':
      return `Assembling final video (${assemblyProgress}%)...`;
    case 'completed':
      return 'Done!';
    case 'failed':
      return error ?? 'Failed';
    default:
      return '';
  }
}

// ============================================
// Hook
// ============================================

export function useGenerateAndAssembleVideo(
  config: IGenerateAndAssembleConfig
): IUseGenerateAndAssembleReturn {
  const {
    adapter,
    isAllScenesReady,
    scenesReadyCount,
    scenesTotalCount,
    failedVideoCount = 0,
    onGeneratePrerequisites,
    onGenerateAllVideos,
    onComplete,
  } = config;

  const [phase, setPhase] = useState<GenerateAndAssemblePhase>('idle');
  const [error, setError] = useState<string | null>(null);

  // Track whether we initiated the pipeline (prevents stale isAllScenesReady from firing)
  const pipelineActiveRef = useRef(false);
  // Track if assembly was already triggered
  const assemblyTriggeredRef = useRef(false);

  // Assembly hook (normalize → concat → upload → record)
  const {
    state: assemblyState,
    generateFullVideo: startAssembly,
    reset: resetAssembly,
  } = useServerFullVideoGenerator(adapter);

  // -------------------------------------------
  // Phase: waiting → assembling
  // When all scenes are ready, trigger assembly.
  // setState is inside the async callback, not at the synchronous effect top-level,
  // so no cascading render is caused by the effect body itself.
  // -------------------------------------------
  useEffect(() => {
    if (
      phase !== 'waiting' ||
      !isAllScenesReady ||
      !pipelineActiveRef.current ||
      assemblyTriggeredRef.current
    ) {
      return;
    }
    assemblyTriggeredRef.current = true;
    void startAssembly()
      .then(() => {
        setPhase('assembling');
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Assembly failed';
        setError(msg);
        setPhase('failed');
      });
  }, [phase, isAllScenesReady, startAssembly]);

  // -------------------------------------------
  // Phase: waiting → failed (stuck detection)
  // When videos have failed during waiting, auto-transition to failed.
  // Delayed to allow reactive queries to reflect retry deletions.
  // setState is inside the async setTimeout callback, not the synchronous effect body.
  // -------------------------------------------
  useEffect(() => {
    if (phase !== 'waiting' || failedVideoCount <= 0 || !pipelineActiveRef.current) return;

    const timer = setTimeout(() => {
      // Re-check refs — pipeline may have advanced during the delay
      if (pipelineActiveRef.current && !assemblyTriggeredRef.current) {
        setError(`${failedVideoCount} scene video(s) failed. Click Retry to regenerate.`);
        setPhase('failed');
        pipelineActiveRef.current = false;
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [phase, failedVideoCount]);

  // -------------------------------------------
  // Phase: assembling → completed / failed
  // Derive effective phase from assemblyState during render — no effect needed.
  // onComplete is fired in an effect that watches the derived transition.
  // -------------------------------------------
  const effectivePhase: GenerateAndAssemblePhase =
    phase === 'assembling' && assemblyState.status === 'completed'
      ? 'completed'
      : phase === 'assembling' && assemblyState.status === 'error'
        ? 'failed'
        : phase;

  const effectiveError =
    phase === 'assembling' && assemblyState.status === 'error' ? assemblyState.error : error;

  // Fire onComplete side-effect when assembly finishes — runs once per transition.
  useEffect(() => {
    if (effectivePhase === 'completed' && phase === 'assembling') {
      pipelineActiveRef.current = false;
      onComplete?.();
    }
    if (effectivePhase === 'failed' && phase === 'assembling') {
      pipelineActiveRef.current = false;
    }
  }, [effectivePhase, phase, onComplete]);

  // -------------------------------------------
  // Main trigger
  // -------------------------------------------
  const generate = useCallback(() => {
    // Reset state
    setError(null);
    resetAssembly();
    pipelineActiveRef.current = true;
    assemblyTriggeredRef.current = false;

    const run = async () => {
      try {
        // Phase 1: Prerequisites (images)
        if (onGeneratePrerequisites) {
          setPhase('prerequisites');
          await onGeneratePrerequisites();
        }

        // Phase 2: Start video + audio generation
        setPhase('generating');
        const result = await onGenerateAllVideos();

        // Phase 3: Decide waiting vs immediate assembly
        // Use the return value — NOT the stale isAllScenesReady closure
        if (result.started > 0) {
          // New videos were kicked off — must wait for them to complete
          setPhase('waiting');
        } else if (isAllScenesReady) {
          // Nothing new started AND all existing scenes are ready — assemble now
          assemblyTriggeredRef.current = true;
          setPhase('assembling');
          await startAssembly();
        } else {
          // Nothing started but scenes aren't ready yet (edge case: muxing in progress)
          setPhase('waiting');
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Generation failed';
        setError(msg);
        setPhase('failed');
        pipelineActiveRef.current = false;
      }
    };

    void run();
  }, [
    onGeneratePrerequisites,
    onGenerateAllVideos,
    isAllScenesReady,
    startAssembly,
    resetAssembly,
  ]);

  // -------------------------------------------
  // Reset
  // -------------------------------------------
  const reset = useCallback(() => {
    setPhase('idle');
    setError(null);
    resetAssembly();
    pipelineActiveRef.current = false;
    assemblyTriggeredRef.current = false;
  }, [resetAssembly]);

  // -------------------------------------------
  // Derived values — use effectivePhase/effectiveError for all outputs
  // -------------------------------------------
  const isGenerating =
    effectivePhase !== 'idle' && effectivePhase !== 'completed' && effectivePhase !== 'failed';

  const overallProgress = computeOverallProgress(
    effectivePhase,
    scenesReadyCount,
    scenesTotalCount,
    assemblyState.progress
  );

  const statusLabel = computeStatusLabel(
    effectivePhase,
    scenesReadyCount,
    scenesTotalCount,
    assemblyState.progress,
    effectiveError
  );

  return {
    generate,
    reset,
    phase: effectivePhase,
    isGenerating,
    statusLabel,
    overallProgress,
    error: effectiveError,
  };
}
